import { IAddressBasicData } from '@df/session/api/address-book/address';
import { AddressSearcher, IAddressSearcherData, IAddressSearcherOptions, IHint } from '@df/ui/form/address-searcher';
import { Nexus } from '@tomandco/nexus-api-types';
import compact from 'lodash-es/compact';
import each from 'lodash-es/each';
import find from 'lodash-es/find';
import keyBy from 'lodash-es/keyBy';
import values from 'lodash-es/values';
import { NPca } from './pca.interfaces';

export interface IPcaHint extends IHint {
    Id: number;
    Type: string;
}

export interface IPcaAddress {
    City: string;
    Company: string;
    Line1: string;
    Line2: string;
    Line3: string;
    Line4: string;
    Line5: string;
    Street: string;
    CountryName: string;
    PostalCode: string;
    ProvinceCode: string;
    ProvinceName: string;
    AdminAreaName: string;
    SubBuilding: string;
    BuildingNumber: string;
    BuildingName: string;
}

export class PcaAddressSearcher extends AddressSearcher {
    protected override _hints: IPcaHint[] = [];

    constructor(
        private pcaOptions: NPca.IModuleOptions,
        addressSearcherData: IAddressSearcherData,
        options?: IAddressSearcherOptions
    ) {
        super(addressSearcherData, options);
    }

    protected override _search(hint?: IPcaHint): void {
        if (hint && hint.Type === 'Address') {
            this.requestService.send(
                this.status,
                {
                    path: 'predict/view',
                    data: {
                        address: hint.Id
                    }
                },
                result => this.applySelection(result.predict[0])
            );
        } else {
            super._search(hint);
        }
    }

    protected runSearch(query: string, hint?: IPcaHint): void {
        const data = this.addressSearcherData.data;

        this.requestService.send(
            this.status,
            {
                path: 'predict/getlist',
                data: {
                    query: query,
                    country: data ? data.country_id : undefined,
                    container: hint ? hint.Id : undefined
                }
            },
            result => {
                this.setHints(query, values(keyBy(result.predict, 'Id')));
            }
        ); // this trick is to get rid of duplicates
    }

    protected override applySelection(address: IPcaAddress) {
        const data = this.addressSearcherData.data;
        if (data) {
            const selection = this.getSelection(address);
            each(selection, (value: any, key: string) => {
                (<string>data[key]) = value;
            });
        }
        super.applySelection();
    }

    protected getSelection(address: IPcaAddress): IAddressBasicData {
        const data: IAddressBasicData = {};

        data.city = address.City;
        // first, cause it is used below
        data.street_2 = this.pushTo('', compact([address.Line5, address.Line4, address.Line3, address.Line2]));
        // pushed items in asc priority
        data.street_1 = this.pushTo(compact([address.Company, address.Line1]).join(', '), [address.Street], [data.street_2]);
        data.country = address.CountryName;
        data.postcode = address.PostalCode;

        if (this.pcaOptions.company) {
            data.company = address.Company;
        }

        const regions: Nexus.Lib.General.Region[] | undefined = this.addressSearcherData.regions;
        if (regions) {
            // try to match region with code or by name
            const findRegion =
                find(regions, region => {
                    const code = normalizeString(region.code);
                    const name = normalizeString(region.name);
                    return (
                        code === normalizeString(address.ProvinceCode) ||
                        code === normalizeString(address.ProvinceName) ||
                        name === normalizeString(address.ProvinceName)
                    );
                }) ||
                find(regions, region => {
                    return normalizeString(region.name) === normalizeString(address.AdminAreaName);
                });

            if (findRegion) {
                data.region_id = findRegion.id;
            }
        } else {
            data.region = address.ProvinceName;
        }

        function normalizeString(value: any) {
            if (value && typeof value === 'string') {
                return value.toLocaleLowerCase();
            }
            return value;
        }

        return data;
    }

    trackByHint(index: number, hint: IPcaHint) {
        return hint.Id;
    }
}
